// Time Cards — manager view.
// Mirrors OnTheClock's Time Cards page: biweekly grid grouped by employee,
// columns Day/Date/TOTAL/REG/OT1/IN/OUT, with edit/delete actions and a
// "Hours Sub Total" row per employee.

import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api';
import EditDayModal from '../components/EditDayModal';
import ModalRoot from '../../components/ModalRoot';

function ymdToday() { return new Date().toISOString().slice(0, 10); }
function fmtTime(s) {
  if (!s) return '—';
  const d = new Date(s + (s.endsWith('Z') ? '' : 'Z'));
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}
function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString([], { month: '2-digit', day: '2-digit', year: '2-digit' });
}
function dayOf(s) {
  if (!s) return '';
  const d = new Date(s + 'T00:00:00');
  return ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
}
function hours(sec) { if (sec == null) return '0.00'; return (sec / 3600).toFixed(2); }

export default function TimeCardsPage() {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState(null);
  const [entries, setEntries] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [err, setErr] = useState('');
  const [addingFor, setAddingFor] = useState(null);

  useEffect(() => {
    api.listPayPeriods().then(rows => {
      setPeriods(rows);
      if (rows[0]) setPeriodId(rows[0].id);
    }).catch(e => setErr(e.message));
  }, []);

  function load() {
    if (!periodId) return;
    api.listEntries({ period_id: periodId }).then(setEntries).catch(e => setErr(e.message));
  }
  useEffect(load, [periodId]);

  // Group entries by user
  const grouped = useMemo(() => {
    const m = new Map();
    for (const e of entries) {
      if (!m.has(e.user_id)) m.set(e.user_id, { user_id: e.user_id, full_name: e.full_name, username: e.username, days: [], totalSec: 0, otSec: 0 });
      const g = m.get(e.user_id);
      g.days.push(e);
      g.totalSec += (e.adjusted_total_seconds ?? e.total_seconds) || 0;
      g.otSec += (e.override_ot1_seconds || 0) + (e.override_ot2_seconds || 0);
    }
    return Array.from(m.values()).sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [entries]);

  const period = periods.find(p => p.id === periodId);

  async function deleteEntry(id) {
    const reason = window.prompt('Reason for deletion (required):');
    if (!reason) return;
    try { await api.deleteEntry(id, reason); load(); } catch (e) { setErr(e.message); }
  }

  return (
    <div className="page time-cards-page">
      <div className="page-header">
        <h2>Time Cards</h2>
        <div className="filter-bar">
          <select value={periodId || ''} onChange={e => setPeriodId(parseInt(e.target.value, 10))}>
            {periods.map(p => <option key={p.id} value={p.id}>{p.start_date} → {p.end_date} ({p.status})</option>)}
          </select>
        </div>
      </div>
      {err && <div className="error-banner">{err}</div>}
      {period && (
        <div className="time-cards-banner">Pay Period: <strong>{period.start_date} thru {period.end_date}</strong></div>
      )}

      {grouped.length === 0 && <div className="empty-state">No time card entries in this period yet.</div>}

      {grouped.map(g => {
        const regSec = g.totalSec - g.otSec;
        return (
          <div key={g.user_id} className="time-cards-group">
            <div className="time-cards-employee">
              <h4>{g.full_name} <span className="muted">@{g.username}</span></h4>
              <button className="btn-secondary" onClick={() => setAddingFor(g)}>+ Add Day</button>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Day</th><th>Date</th><th>TOTAL</th><th>REG</th><th>OT1</th><th>IN</th><th>OUT</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {g.days.map(d => {
                  const t = (d.adjusted_total_seconds ?? d.total_seconds) || 0;
                  const ot = (d.override_ot1_seconds || 0);
                  return (
                    <tr key={d.id}>
                      <td>{dayOf((d.clock_in_at || '').slice(0, 10))}</td>
                      <td>{fmtDate((d.clock_in_at || '').slice(0, 10))}</td>
                      <td><strong>{hours(t)}</strong></td>
                      <td>{hours(Math.max(0, t - ot))}</td>
                      <td>{hours(ot)}</td>
                      <td className="punch-in">{fmtTime(d.clock_in_at)}</td>
                      <td className="punch-out">{fmtTime(d.clock_out_at)}</td>
                      <td><span className={`status-pill status-${d.status}`}>{d.status}</span></td>
                      <td>
                        <button className="btn-icon" title="Edit" onClick={() => setEditingId(d.id)}>✎</button>
                        <button className="btn-icon btn-icon-danger" title="Delete" onClick={() => deleteEntry(d.id)}>×</button>
                      </td>
                    </tr>
                  );
                })}
                <tr className="time-cards-subtotal">
                  <td colSpan={2}><strong>Hours Sub Total</strong></td>
                  <td><strong>{hours(g.totalSec)}</strong></td>
                  <td>{hours(Math.max(0, g.totalSec - g.otSec))}</td>
                  <td>{hours(g.otSec)}</td>
                  <td colSpan={4}></td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      })}

      {editingId && (
        <EditDayModal entryId={editingId} onClose={() => setEditingId(null)} onSaved={load} />
      )}
      {addingFor && (
        <AddDayModal user={addingFor} period={period} onClose={() => setAddingFor(null)} onSaved={() => { setAddingFor(null); load(); }} />
      )}
    </div>
  );
}

function AddDayModal({ user, period, onClose, onSaved }) {
  const [date, setDate] = useState(period?.start_date || ymdToday());
  const [mode, setMode] = useState('punch');  // punch | hours
  const [punchIn, setPunchIn] = useState('08:00');
  const [punchOut, setPunchOut] = useState('16:00');
  const [hoursVal, setHoursVal] = useState(8);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');

  async function submit() {
    if (!reason) { setErr('Reason required'); return; }
    try {
      const body = { user_id: user.user_id, day_notes: notes, reason };
      if (mode === 'punch') {
        body.clock_in_at = `${date} ${punchIn}:00`;
        body.clock_out_at = `${date} ${punchOut}:00`;
      } else {
        body.clock_in_at = `${date} 00:00:00`;
        body.use_hours = true;
        body.use_hours_total_seconds = Math.round((parseFloat(hoursVal) || 0) * 3600);
      }
      await api.addDay(body);
      onSaved();
    } catch (e) { setErr(e.message); }
  }

  return (
    <ModalRoot onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add Day — {user.full_name}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {err && <div className="form-error">{err}</div>}
          <label>Date <input type="date" value={date} onChange={e => setDate(e.target.value)} /></label>
          <div className="form-group">
            <label><input type="radio" checked={mode === 'punch'} onChange={() => setMode('punch')} /> Punch in/out</label>
            <label><input type="radio" checked={mode === 'hours'} onChange={() => setMode('hours')} /> Manual hours (no times)</label>
          </div>
          {mode === 'punch' ? (
            <div className="edit-day-grid edit-day-grid-2">
              <label>In <input type="time" value={punchIn} onChange={e => setPunchIn(e.target.value)} /></label>
              <label>Out <input type="time" value={punchOut} onChange={e => setPunchOut(e.target.value)} /></label>
            </div>
          ) : (
            <label>Hours <input type="number" step="0.25" value={hoursVal} onChange={e => setHoursVal(e.target.value)} /></label>
          )}
          <label>Notes <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} /></label>
          <label>Reason (required) <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="Why is this being added manually?" /></label>
          <div className="form-actions">
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn-primary" onClick={submit}>Add Day</button>
          </div>
        </div>
      </div>
    </ModalRoot>
  );
}
